import { useLayoutEffect, useRef } from 'react';
import Navbar from '../../components/Navbar/Navbar';
import HeaderImage from '../../../images/charco-inbox.svg';
import * as Styled from './LegalStyles';
import {useLocation} from 'react-router-dom';
import FooterNav from '../../components/FooterNav/FooterNav';
import ScrollTopIcon from '../../../images/arrow-up-circle.svg';
import Meta from '../../components/Meta/Meta';

const TermsOfService = () => {
  const location = useLocation();
  const pageRef = useRef(null);
  const navLinks = [{
    title: 'Privacy Policy',
    link: '/privacy'
  }, {
    title: 'Terms of Service',
    link: '/tos'
  }, {
    title: 'Security Practices',
    link: '/security'
  }]
  useLayoutEffect(() => {
    if (!location.hash) {
      return;
    }
    if (pageRef && pageRef.current) {
      // @ts-ignore
      const el = pageRef.current.querySelector(location.hash);
      el.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
    }
  }, [location.hash]);

  return (
    <section>
      <Meta page="tos" />
      <Navbar
        title="Privacy & Terms"
        links={navLinks}
      />
      <Styled.PageWrapper ref={pageRef}>
        <a href="#top" className="ScrollTopButton">
          <Styled.ScrollTopIcon src={ScrollTopIcon} />
        </a>
        <Styled.H1 id="top">
          Terms of Service
        </Styled.H1>
        <Styled.Heading>
          <Styled.Img src={HeaderImage} alt="terms of service" />
          <Styled.H4>
            We know it’s tempting to skip these Terms of Service, but it’s important to establish what you can expect from us as you use Trova services, and what we expect from you.
          </Styled.H4>
          <Styled.Subtitle>
            Effective date, June 1, 2021
          </Styled.Subtitle>
        </Styled.Heading>
        <Styled.H3>
          What you need to know in a few words
        </Styled.H3>
        <Styled.BulletList>
          <Styled.BulletListItem>
            By using Trova, you agree to respect the Terms of Service. Please don’t abuse them. Abuses may result in the cancellation of your account and/or settling any dispute by arbitration.
          </Styled.BulletListItem>
          <Styled.BulletListItem>
            Please use accounts that you are authorized to use and connect.
          </Styled.BulletListItem>
          <Styled.BulletListItem>
            Trova does not and will not sell you data, ever. Please read our <a href='/privacy'>Privacy Policy</a> to understand which information we collect, use, and disclose from our users.
          </Styled.BulletListItem>
          <Styled.BulletListItem>
            We may update our Terms of Service at our discretion and we will always keep you informed if we do. Please check our updates.
          </Styled.BulletListItem>
          <Styled.BulletListItem>
            We are always happy to hear your thoughts (“Feedback”). When you submit comments, ideas, or suggestions, you agree that Trova can freely use your feedback for any reason without any restriction and/or compensation.
          </Styled.BulletListItem>
        </Styled.BulletList>
        <Styled.Paragraph>
        </Styled.Paragraph>
        <Styled.Paragraph>
          Welcome to the Trova AI, Inc website located at <a href="/">https://www.gotrova.ai</a> (the “Site”). Please read these Terms of Service (the “Terms”) and our Privacy Policy <a href="/privacy">https://www.gotrova.ai/privacy</a> (“Privacy Policy”) carefully because they govern your use of our Site and related services accessible via our Site.
        </Styled.Paragraph>
        <Styled.H3>
          Table of contents
        </Styled.H3>
        <Styled.PageLink to="#agreement">
          Agreement to terms
        </Styled.PageLink>
        <Styled.PageLink to="#policy">
          Privacy Policy
        </Styled.PageLink>
        <Styled.PageLink to="#changes">
          Changes to Terms or Site
        </Styled.PageLink>
        <Styled.PageLink to="#siteusage">
          Who May Use the Site
        </Styled.PageLink>
        <Styled.PageLink to="#feedback">
          Feedback
        </Styled.PageLink>
        <Styled.PageLink to="#prohibitions">
          General Prohibitions and Trova AI, Inc’s Enforcement Rights
        </Styled.PageLink>
        <Styled.PageLink to="#links">
          Links to Third Party Websites or Resources
        </Styled.PageLink>
        <Styled.PageLink to="#termination">
          Termination
        </Styled.PageLink>
        <Styled.PageLink to="#disclaimers">
          Warranty Disclaimers
        </Styled.PageLink>
        <Styled.PageLink to="#indemnity">
          Indemnity
        </Styled.PageLink>
        <Styled.PageLink to="#limitation">
          Limitation of Liability
        </Styled.PageLink>
        <Styled.PageLink to="#geverningLaw">
          Governing Law and Forum Choice
        </Styled.PageLink>
        <Styled.PageLink to="#dispute">
          Dispute resolution
        </Styled.PageLink>
        <Styled.PageLink to="#generalTerms">
          General Terms
        </Styled.PageLink>
        <Styled.PageLink to="#contact">
          Contact Information
        </Styled.PageLink>

        <Styled.H4 id="agreement">
          Agreement to Terms
        </Styled.H4>
        <Styled.Paragraph>
          By using our Site, you agree to be bound by these Terms. If you are accessing and using the Site on behalf of a company (such as your employer) or other legal entity, you represent and warrant that you have the authority to bind that company or other legal entity to these Terms. In that case, “you” and “your” will refer to that company or other legal entity. When you agree to these terms you are also agreeing (with limited exception) to resolve any dispute between you and Trova AI, Inc through binding, individual arbitration rather than in court. Please review carefully Section 13 below for details regarding arbitration (including the procedure to opt out of arbitration).
        </Styled.Paragraph>
        <Styled.H4 id="policy">
          Privacy Policy
        </Styled.H4>
        <Styled.Paragraph>
          Please refer to our <a href="/privacy">Privacy Policy</a> (<a href="https://gotrova.ai/privacy">https://gotrova.ai/privacy</a>) for information on how we collect, use and disclose information from our users. You acknowledge and agree that your use of the Site is subject to our <a href="/privacy">Privacy Policy</a>.
        </Styled.Paragraph>
        <Styled.H4 id="changes">
          Changes to Terms or Site
        </Styled.H4>
        <Styled.Paragraph>
          We may update the Terms at any time, in our sole discretion. If we do so, we will let you know either by posting the updated Terms on the Site or through other communications. It’s important that you review the Terms whenever we update them or you use the Site. If you continue to use the Site after we have posted updated Terms, you are agreeing to be bound by the updated Terms. If you don’t agree to be bound by the updated Terms, then, except as otherwise provided in Section 13(b), you may not use the Site anymore. Because our Site is evolving over time we may change or discontinue all or any part of the Site, at any time and without notice, at our sole discretion.
        </Styled.Paragraph>
        <Styled.H4 id="siteusage">
          Who May Use the Site
        </Styled.H4>
        <Styled.Paragraph>
          You may use the Site and related services only if you are 18 years or older and capable of forming a binding contract with Trova AI, Inc and are not barred from using the Site under applicable law.
        </Styled.Paragraph>
        <Styled.H4 id="feedback">
          Feedback
        </Styled.H4>
        <Styled.Paragraph>
          We welcome feedback, comments and suggestions for improvements to the Site (“Feedback”). You can submit a Feedback form on the website or by emailing us at <a href="mailto:info@gotrova.ai">info@gotrova.ai</a>. You grant to us a non-exclusive, transferable, worldwide, perpetual, irrevocable, fully-paid, royalty-free license, with the right to sublicense, copy, modify and create derivative works based upon and otherwise exploit the Feedback for any purpose.
        </Styled.Paragraph>
        <Styled.H4 id="prohibitions">
          General Prohibitions and Trova AI, Inc’s Enforcement Rights
        </Styled.H4>
        <Styled.Paragraph>
          You agree not to do any of the following:
        </Styled.Paragraph>
        <Styled.Paragraph>
          (a) Access, tamper with, or use non-public areas of the Site, Trova AI, Inc’s computer systems, or the technical delivery systems of Trova AI, Inc’s providers;
        </Styled.Paragraph>
        <Styled.Paragraph>
          (b) Attempt to probe, scan or test the vulnerability of any Trova AI, Inc system or network or breach any security or authentication measures;
        </Styled.Paragraph>
        <Styled.Paragraph>
          (c) Avoid, bypass, remove, deactivate, impair, descramble or otherwise circumvent any technological measure implemented by Trova AI, Inc or any of Trova AI, Inc’s providers or any other third party (including another user) to protect the Site
        </Styled.Paragraph>
        <Styled.Paragraph>
          (d) Attempt to access or search the Site through the use of any engine, software, tool, agent, device or mechanism (including spiders, robots, crawlers, data mining tools or the like) other than the software and/or search agents provided by Trova AI, Inc or other generally available third-party web browsers;
        </Styled.Paragraph>
        <Styled.Paragraph>
          (e)   Send any unsolicited or unauthorized advertising, promotional materials, email, junk mail, spam, chain letters or other form of solicitation;
        </Styled.Paragraph>
        <Styled.Paragraph>
          (f) Use any meta tags or other hidden text or metadata utilizing a Trova AI, Inc trademark, logo URL or product name without Trova AI, Inc’s express written consent;
        </Styled.Paragraph>
        <Styled.Paragraph>
          (g) Use the Site, or any portion thereof, for any commercial purpose or for the benefit of any third party or in any manner not permitted by these Terms;
        </Styled.Paragraph>
        <Styled.Paragraph>
          (h) Attempt to decipher, decompile, disassemble or reverse engineer any of the software used to provide the Site;
        </Styled.Paragraph>
        <Styled.Paragraph>
          (i) Interfere with, or attempt to interfere with, the access of any user, host or network, including, without limitation, sending a virus, overloading, flooding, spamming, or mail-bombing the Site;
        </Styled.Paragraph>
        <Styled.Paragraph>
          (j) Collect or store any personally identifiable information from the Site from other users of the Site without their express permission;
        </Styled.Paragraph>
        <Styled.Paragraph>
          (l)  Violate any applicable law or regulation; or
        </Styled.Paragraph>
        <Styled.Paragraph>
          (m) Encourage or enable any other individual to do any of the foregoing.
        </Styled.Paragraph>
        <Styled.Paragraph>
          Although we’re not obligated to monitor access to or use of the Site, we have the right to do so for the purpose of operating the Site, to ensure compliance with these Terms and to comply with applicable law or other legal requirements. We reserve the right, but are not obligated, to disable access to the Site, at any time and without notice, including, but not limited to, if we, at our sole discretion, consider your use of the Site to be objectionable or in violation of these Terms. We have the right to investigate violations of these Terms or conduct that affects the Site. We may also consult and cooperate with law enforcement authorities to prosecute users who violate the law.
        </Styled.Paragraph>
        <Styled.H4 id="links">
          Links to Third Party Websites or Resources
        </Styled.H4>
        <Styled.Paragraph>
          The Site may contain links to third-party websites or resources. We provide these links only as a convenience and are not responsible for the content, products or Site on or available from those websites or resources or links displayed on such websites. You acknowledge sole responsibility for and assume all risk arising from, your use of any third-party websites or resources.
        </Styled.Paragraph>
        <Styled.H4 id="termination">
          Termination
        </Styled.H4>
        <Styled.Paragraph>
          We may terminate your access to and use of the Site, at our sole discretion, at any time and without notice to you. Upon any termination, discontinuation or cancellation of your access to the Site, the following Sections will survive: Warranty Disclaimers, Limitation of Liability, Governing Law and Forum, Dispute Resolution Choice, and General Terms.
        </Styled.Paragraph>
        <Styled.H4 id="disclaimers">
          Warranty Disclaimers
        </Styled.H4>
        <Styled.Paragraph>
          THE SITE AND RELATED SERVICES ARE PROVIDED “AS IS,” WITHOUT WARRANTY OF ANY KIND. WITHOUT LIMITING THE FOREGOING, WE EXPLICITLY DISCLAIM ANY IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, QUIET ENJOYMENT AND NON-INFRINGEMENT, AND ANY WARRANTIES ARISING OUT OF COURSE OF DEALING OR USAGE OF TRADE. We make no warranty that the Site will meet your requirements or be available on an uninterrupted, secure, or error-free basis.
        </Styled.Paragraph>
        <Styled.H4 id="indemnity">
          Indemnity
        </Styled.H4>
        <Styled.Paragraph>
          You will indemnify and hold harmless Trova AI, Inc and its officers, directors, employees and agents, from and against any claims, disputes, demands, liabilities, damages, losses, and costs and expenses, including, without limitation, reasonable legal and accounting fees arising out of or in any way connected with (i) your access to or use of the Site or (ii) your violation of these Terms.
        </Styled.Paragraph>
        <Styled.H4 id="limitation">
          Limitation of Liability
        </Styled.H4>
        <Styled.Paragraph>
          (a) NEITHER TROVA AI, INC NOR ANY OTHER PARTY INVOLVED IN CREATING, PRODUCING, OR DELIVERING ACCESS TO THE SITE WILL BE LIABLE FOR ANY INCIDENTAL, SPECIAL, EXEMPLARY OR CONSEQUENTIAL DAMAGES, OR DAMAGES FOR LOST PROFITS, LOST REVENUES, LOST SAVINGS, LOST BUSINESS OPPORTUNITY, LOSS OF DATA OR GOODWILL, SERVICE INTERRUPTION, COMPUTER DAMAGE OR SYSTEM FAILURE OR THE COST OF SUBSTITUTE SITE OF ANY KIND ARISING OUT OF OR IN CONNECTION WITH THESE TERMS OR FROM THE USE OF OR INABILITY TO USE THE SITE, WHETHER BASED ON WARRANTY, CONTRACT, TORT (INCLUDING NEGLIGENCE), PRODUCT LIABILITY OR ANY OTHER LEGAL THEORY, AND WHETHER OR NOT TROVA AI, INC OR ANY OTHER PARTY HAS BEEN INFORMED OF THE POSSIBILITY OF SUCH DAMAGE, EVEN IF A LIMITED REMEDY SET FORTH HEREIN IS FOUND TO HAVE FAILED OF ITS ESSENTIAL PURPOSE.
        </Styled.Paragraph>
        <Styled.Paragraph>
          (b) IN NO EVENT WILL TROVA AI, INC’S TOTAL LIABILITY ARISING OUT OF OR IN CONNECTION WITH THESE TERMS OR FROM THE USE OF OR INABILITY TO USE THE SITE EXCEED THE AMOUNTS YOU HAVE PAID TO TROVA AI, INC FOR USE OF THE SITE OR TEN THOUSAND DOLLARS ($10,000), IF YOU HAVE NOT HAD ANY PAYMENT OBLIGATIONS TO TROVA AI, INC, AS APPLICABLE.
        </Styled.Paragraph>
        <Styled.Paragraph>
          (c) THE EXCLUSIONS AND LIMITATIONS OF DAMAGES SET FORTH ABOVE ARE FUNDAMENTAL ELEMENTS OF THE BASIS OF THE BARGAIN BETWEEN TROVA AI, INC AND YOU.
        </Styled.Paragraph>
        <Styled.H4 id="geverningLaw">
          Governing Law and Forum Choice
        </Styled.H4>
        <Styled.Paragraph>
          These Terms and any action related thereto will be governed by the Federal Arbitration Act, federal arbitration law, and the laws of the State of New York, without regard to its conflict of laws provisions. Except as otherwise expressly set forth in Section 13 the exclusive jurisdiction for all Disputes (defined below) that you and Trova AI, Inc are not required to arbitrate will be the state and federal courts located in the courts of the state of New York, and you and Trova AI, Inc each waive any objection to jurisdiction and venue in such courts.
        </Styled.Paragraph>
        <Styled.H4 id="dispute">
          Dispute resolution
        </Styled.H4>
        <Styled.Paragraph>
          (a) Mandatory Arbitration of Disputes. We each agree that any dispute, claim or controversy arising out of or relating to these Terms or the breach, termination, enforcement, interpretation or validity thereof or the use of the Site (collectively, “Disputes”) will be resolved solely by binding, individual arbitration. You and Trova AI, Inc agree that the U.S. Federal Arbitration Act governs the interpretation and enforcement of these Terms, and that you and Trova AI, Inc are each waiving the right to a trial by jury or to participate in a class action. This arbitration provision shall survive termination of these Terms.
        </Styled.Paragraph>
        <Styled.Paragraph>
          (b) Exceptions and Opt-out. As limited exceptions to Section 16(a) above: (i) you may seek to resolve a Dispute in small claims court if it qualifies; and (ii) Trova AI, Inc retains the right to seek injunctive or other equitable relief from a court to prevent (or enjoin) the infringement or misappropriation of our intellectual property rights. In addition, you will retain the right to opt out of arbitration entirely and litigate any Dispute if you provide us with written notice of your desire to do so by email at <a href="mailto:info@gotrova.ai">info@gotrova.ai</a> or by regular mail at Trova AI, Inc 60 Hazelwood Dr. Champaign, Illinois, 61820 within thirty (30) days following the date you first agree to these Terms.
        </Styled.Paragraph>
        <Styled.H4 id="generalTerms">
          General Terms
        </Styled.H4>
        <Styled.Paragraph>
          (a) Severability. If any provision of these Terms is held invalid or unenforceable by an arbitrator or a court of competent jurisdiction, that provision will be enforced to the maximum extent permissible and the other provisions of these Terms will remain in full force and effect.
        </Styled.Paragraph>
        <Styled.Paragraph>
          (b) Entire Agreement. These Terms constitute the entire and exclusive understanding and agreement between Trova AI, Inc and you regarding the Site, and these Terms supersede and replace any and all prior oral or written understandings or agreements between Trova AI, Inc and you regarding the Site.
        </Styled.Paragraph>
        <Styled.Paragraph>
          (c) Assignment. You may not assign or transfer these Terms, by operation of law or otherwise, without Trova AI, Inc’s prior written consent. Any attempt by you to assign or transfer these Terms, without such consent, will be null. Trova AI, Inc may freely assign or transfer these Terms without restriction. Subject to the foregoing, these Terms will bind and inure to the benefit of the parties, their successors and permitted assigns.
        </Styled.Paragraph>
        <Styled.Paragraph>
          (d) Notices. Any notices or other communications provided by Trova AI, Inc under these Terms, including those regarding modifications to these Terms, will be given: (i) via email; or (ii) by posting to the Site. For notices made by e-mail, the date of receipt will be deemed the date on which such notice is transmitted.
        </Styled.Paragraph>
        <Styled.Paragraph>
          e) Waiver of Rights. Trova AI, Inc’s failure to enforce any right or provision of these Terms will not be considered a waiver of such right or provision. The waiver of any such right or provision will be effective only if in writing and signed by a duly authorized representative of Trova AI, Inc. Except as expressly set forth in these Terms, the exercise by either party of any of its remedies under these Terms will be without prejudice to its other remedies under these Terms or otherwise.
        </Styled.Paragraph>
        <Styled.H4 id="contact">
          Contact Information
        </Styled.H4>
        <Styled.Paragraph>
          If you have questions or need to contact us about these Terms or the Site, please email us at <a href="mailto:info@gotrova.ai">info@gotrova.ai</a>. You can reach Trova AI, Inc by mail at:
        </Styled.Paragraph>
        <Styled.Paragraph>
          Trova AI, Inc
        </Styled.Paragraph>
        <Styled.Paragraph>
          60 Hazelwood Dr,
        </Styled.Paragraph>
        <Styled.Paragraph>
          Champaign, Illinois, 61820
        </Styled.Paragraph>
      </Styled.PageWrapper>
      <FooterNav />
    </section>
  )
}

export default TermsOfService;